import React from "react";
import cls from "classnames";
import { Avatar, Button, Icon, Text } from "@zorni/components-lib";

import AdminStatus from "../../../common/adminStatus/AdminStatus";
import { canArchiveConversation, canCloseConversation } from "../utils/conversationActions";
import styles from "./InboxTable.module.css";

/**
 * @typedef {Object} ConversationItem
 * @property {string} id
 * @property {string=} tripReference
 * @property {string=} establishmentId
 * @property {string=} customerId
 * @property {string=} assignedAgentId
 * @property {string[]=} participants
 * @property {{ content: string, sendingDate: string }=} lastMessage
 * @property {"OPEN" | "CLOSED" | "ARCHIVED"} status
 */

const formatRelativeAge = (isoString) => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "-";

  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 1) return "À l’instant";
  if (diffMinutes < 60) return `Il y a ${diffMinutes}m`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `Il y a ${diffHours}h`;

  const diffDays = Math.floor(diffHours / 24);
  return `Il y a ${diffDays}j`;
};

const formatTime = (isoString) => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
};

const formatReferenceBadge = (reference) => {
  if (!reference) return "-";
  const clean = String(reference).startsWith("#")
    ? String(reference).slice(1)
    : String(reference);
  return `#${clean}`;
};

const getAgentLabel = (agent, assignedAgentId) => {
  if (agent) {
    const name = `${agent.firstName || ""} ${agent.lastName || ""}`.trim();
    if (name) return name;
    if (agent.email) return agent.email;
  }
  return assignedAgentId;
};

const ActionIconButton = ({ iconName, label, onClick, disabled, className }) => {
  return (
    <button
      type="button"
      className={cls(styles.iconAction, disabled && styles.iconActionDisabled, className)}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      <Icon name={iconName} size="sm" />
    </button>
  );
};

/**
 * InboxTable — table des conversations (admin).
 *
 * Colonnes : Référence, Dernière activité, Propriétaire, Status, Gestion.
 *
 * @param {{
 *  items?: ConversationItem[];
 *  loading?: boolean;
 *  userId?: string;
 *  agentsById?: Map<string, { id: string, firstName?: string, lastName?: string, email?: string }>;
 *  actionsDisabled?: boolean;
 *  className?: string;
 *  onOpen?: (item: ConversationItem) => void;
 *  onAssign?: (item: ConversationItem) => void;
 *  onArchive?: (item: ConversationItem) => void;
 *  onClose?: (item: ConversationItem) => void;
 *  emptyLabel?: string;
 * }} props
 */
const InboxTable = ({
  items = [],
  loading = false,
  userId,
  agentsById,
  actionsDisabled = false,
  className,
  onOpen,
  onAssign,
  onArchive,
  onClose,
  emptyLabel = "Aucune conversation à afficher.",
}) => {
  if (!loading && !items.length) {
    return (
      <div className={styles.emptyState}>
        <Text size="sm" color="muted">
          {emptyLabel}
        </Text>
      </div>
    );
  }

  return (
    <div className={cls(styles.tableWrapper, className)}>
      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            <th>Référence</th>
            <th>Dernière activité</th>
            <th>Propriétaire</th>
            <th>Status</th>
            <th className={styles.actionsHeader}>Gestion</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const isUnassigned = !item.assignedAgentId;
            const isMine = item.assignedAgentId === userId;
            const agent = item.assignedAgentId
              ? agentsById?.get(item.assignedAgentId)
              : null;
            const isRowClickable = Boolean(onOpen);

            return (
              <tr
                key={item.id}
                className={cls(styles.rowHover, isRowClickable && styles.clickableRow)}
                onClick={isRowClickable ? () => onOpen(item) : undefined}
              >
                <td>
                  <span className={styles.referenceBadge}>
                    {formatReferenceBadge(item.tripReference)}
                  </span>
                </td>

                <td className={styles.activityCell}>
                  <Text as="div" size="sm" weight="medium" className={styles.activityMessage}>
                    {item?.lastMessage?.content || "-"}
                  </Text>
                  {item?.lastMessage?.sendingDate ? (
                    <div className={cls(styles.activityMeta, styles.activityMetaMuted)}>
                      <span>
                        {formatRelativeAge(item.lastMessage.sendingDate)} à{" "}
                        {formatTime(item.lastMessage.sendingDate)}
                      </span>
                    </div>
                  ) : null}
                </td>

                <td>
                  {item.assignedAgentId ? (
                    <div className={styles.ownerCell}>
                      <Avatar
                        name={getAgentLabel(agent, item.assignedAgentId)}
                        size="sm"
                        className={styles.ownerAvatar}
                      />
                      <Text as="span" size="xs" weight="bold" className={styles.ownerName}>
                        {getAgentLabel(agent, item.assignedAgentId).toUpperCase()}
                      </Text>
                    </div>
                  ) : (
                    <Text as="span" size="sm" color="muted" className={styles.ownerUnassigned}>
                      Non affecté
                    </Text>
                  )}
                </td>

                <td>
                  <AdminStatus status={item.status} />
                </td>

                <td>
                  <div className={styles.actions}>
                    {isUnassigned ? (
                      <Button
                        size="small"
                        variant="primary"
                        background="#0f172a"
                        hoverColor="#2563eb"
                        textColor="#ffffff"
                        iconName="iconUserPlus"
                        iconPosition="left"
                        label="Me l'assigner"
                        disabled={actionsDisabled || !onAssign}
                        onClick={(event) => {
                          event.stopPropagation();
                          onAssign?.(item);
                        }}
                      />
                    ) : isMine ? (
                      <div className={styles.iconActions}>
                        <ActionIconButton
                          iconName="iconSend"
                          label="Répondre"
                          className={styles.iconActionPrimary}
                          disabled={actionsDisabled || !onOpen}
                          onClick={(event) => {
                            event.stopPropagation();
                            onOpen?.(item);
                          }}
                        />
                        {/* Une seule action de statut à la fois, selon le statut courant :
                            ouverte -> clôturer ; clôturée -> archiver ; archivée -> aucune
                            (cf. conversationActions.js, règle partagée avec useInbox). */}
                        {canArchiveConversation(item.status) && (
                          <ActionIconButton
                            iconName="iconArchive"
                            label="Archiver"
                            className={styles.iconActionDanger}
                            disabled={actionsDisabled || !onArchive}
                            onClick={(event) => {
                              event.stopPropagation();
                              onArchive?.(item);
                            }}
                          />
                        )}
                        {canCloseConversation(item.status) && (
                          <ActionIconButton
                            iconName="iconLock"
                            label="Clôturer"
                            className={styles.iconActionSuccess}
                            disabled={actionsDisabled || !onClose}
                            onClick={(event) => {
                              event.stopPropagation();
                              onClose?.(item);
                            }}
                          />
                        )}
                      </div>
                    ) : (
                      <Text as="span" size="sm" color="muted" className={styles.ownerUnassigned}>
                        —
                      </Text>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default InboxTable;
export { InboxTable };
